import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { Autorenew as FilterOffIcon } from "@material-ui/icons";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { getUsersPaginated } from "../../../api/user";
import { getUserTypes } from "../../../api/userType";
import { getProvincias } from "../../../api/provincia";
import { alertError } from "../../../utils/logger";
import { dateFormatter } from "../tables/table";
import Select from "react-select";

const PAGE_SIZE = 100;
const initialFilters = {
  active: false,
  tipoUsuario: [],
  fechaAltaDesde: "",
  fechaAltaHasta: "",
  provincia: null,
  numeroSocio: "",
};

function getData(users, types) {
  const data = [];
  if (!users || !Array.isArray(users)) return data;
  for (let i = 0; i < users.length; i++) {
    if (users[i] && users[i].role === "user") {
      const elem = {
        name: users[i].fullName,
        apellido1: users[i].apellido1,
        apellido2: users[i].apellido2,
        email: users[i].email,
        type: types?.find((x) => x._id === users[i].type)?.titulo,
        createdAt: users[i].createdAt,
        id: users[i]._id,
      };
      data.push(elem);
    }
  }
  return data;
}

const NewsletterUsersSelectDialog = (props) => {
  const { open, setOpen, handleSend, templateId, newsletterId, onCancel } = props;

  const [filterOptions, setFilterOptions] = useState(initialFilters);
  const [appliedFilterOptions, setAppliedFilterOptions] =
    useState(initialFilters);
  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSelectAll, setLoadingSelectAll] = useState(false);

  const fetchUsers = useCallback(() => {
    if (!open) return;
    setLoading(true);
    const params = {
      page: currentPage,
      limit: PAGE_SIZE,
      role: "user",
      onlyActive: true, // Solo usuarios activos en listado newsletter (inactivos no reciben envíos)
    };
    if (appliedFilterOptions.active) params.active = true;
    if (appliedFilterOptions.tipoUsuario?.length)
      params.tipoUsuario = appliedFilterOptions.tipoUsuario;
    if (appliedFilterOptions.provincia)
      params.provincia = appliedFilterOptions.provincia;
    if (appliedFilterOptions.fechaAltaDesde)
      params.fechaAltaDesde = appliedFilterOptions.fechaAltaDesde;
    if (appliedFilterOptions.fechaAltaHasta)
      params.fechaAltaHasta = appliedFilterOptions.fechaAltaHasta;
    if (appliedFilterOptions.numeroSocio)
      params.numeroSocio = appliedFilterOptions.numeroSocio;
    if (appliedSearchText) params.search = appliedSearchText;
    params.sortBy = "createdAt";
    params.sortOrder = "desc";

    getUsersPaginated(params)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res.data.data || res.data;
          const pagination = res.data.pagination;
          setData(Array.isArray(responseData) ? responseData : []);
          if (pagination) {
            setTotalPages(pagination.pages || 1);
            setTotalUsers(pagination.total || 0);
          } else {
            setTotalPages(1);
            setTotalUsers(responseData?.length || 0);
          }
        }
      })
      .catch((err) => {
        alertError({
          error: err,
          customMessage: "No se pudieron cargar los usuarios.",
        });
      })
      .finally(() => setLoading(false));
  }, [
    open,
    currentPage,
    appliedFilterOptions,
    appliedSearchText,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (open) {
      getUserTypes().then((res) => setUserTypes(res.data || []));
      getProvincias().then((res) => {
        if (res.status === 200) setProvincias(res.data || []);
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setCurrentPage(1);
      setFilterOptions(initialFilters);
      setAppliedFilterOptions(initialFilters);
      setSearchText("");
      setAppliedSearchText("");
    }
  }, [open]);

  const handleSearch = () => {
    setAppliedFilterOptions({ ...filterOptions });
    setAppliedSearchText(searchText);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setAppliedFilterOptions(initialFilters);
    setSearchText("");
    setAppliedSearchText("");
    setCurrentPage(1);
  };

  const fetchAllUserIds = useCallback(async () => {
    setLoadingSelectAll(true);
    const baseParams = {
      limit: PAGE_SIZE,
      role: "user",
      onlyActive: true,
    };
    if (appliedFilterOptions.active) baseParams.active = true;
    if (appliedFilterOptions.tipoUsuario?.length)
      baseParams.tipoUsuario = appliedFilterOptions.tipoUsuario;
    if (appliedFilterOptions.provincia)
      baseParams.provincia = appliedFilterOptions.provincia;
    if (appliedFilterOptions.fechaAltaDesde)
      baseParams.fechaAltaDesde = appliedFilterOptions.fechaAltaDesde;
    if (appliedFilterOptions.fechaAltaHasta)
      baseParams.fechaAltaHasta = appliedFilterOptions.fechaAltaHasta;
    if (appliedFilterOptions.numeroSocio)
      baseParams.numeroSocio = appliedFilterOptions.numeroSocio;
    if (appliedSearchText) baseParams.search = appliedSearchText;
    baseParams.sortBy = "createdAt";
    baseParams.sortOrder = "desc";

    const allIds = [];
    const pagesToFetch = totalPages > 0 ? totalPages : 1;

    try {
      for (let page = 1; page <= pagesToFetch; page++) {
        const res = await getUsersPaginated({ ...baseParams, page });
        if (res.status === 200) {
          const responseData = res.data.data || res.data;
          const users = Array.isArray(responseData) ? responseData : [];
          const pageIds = getData(users, userTypes).map((r) => r.id);
          allIds.push(...pageIds);
        }
      }
      setSelectedIds(allIds);
    } catch (err) {
      alertError({
        error: err,
        customMessage: "No se pudieron cargar todos los usuarios.",
      });
    } finally {
      setLoadingSelectAll(false);
    }
  }, [
    totalPages,
    appliedFilterOptions,
    appliedSearchText,
    userTypes,
  ]);

  const handleSelectAll = (isSelect) => {
    if (isSelect) {
      fetchAllUserIds();
    } else {
      setSelectedIds([]);
    }
  };

  const tableData = getData(data, userTypes);
  const currentPageIds = tableData.map((r) => r.id);

  const selectRow = {
    mode: "checkbox",
    clickToSelect: true,
    selected: selectedIds,
    onSelect: (row, isSelect) => {
      setSelectedIds((prev) =>
        isSelect ? [...prev, row.id] : prev.filter((id) => id !== row.id)
      );
    },
    onSelectAll: handleSelectAll,
    selectionHeaderRenderer: ({ checked, indeterminate }) => (
      <div style={{ display: "flex", justifyContent: "center", paddingLeft: 8 }}>
        <Checkbox
          color="primary"
          checked={checked}
          indeterminate={indeterminate}
          disabled={loadingSelectAll}
        />
      </div>
    ),
    selectionRenderer: ({ checked }) => (
      <div style={{ display: "flex", justifyContent: "center", paddingLeft: 8 }}>
        <Checkbox color="primary" checked={checked} />
      </div>
    ),
    columnStyle: { width: 60 },
  };

  const columns = [
    { dataField: "name", text: "Nombre", sort: true },
    { dataField: "apellido1", text: "Apellido 1", sort: true },
    { dataField: "apellido2", text: "Apellido 2", sort: true },
    { dataField: "email", text: "Email", sort: true },
    { dataField: "type", text: "Tipo Usuario", sort: true },
    {
      dataField: "createdAt",
      text: "Fecha alta",
      sort: true,
      formatter: (cell) => (cell ? dateFormatter(cell) : ""),
    },
  ];

  const renderFiltersContent = () => (
    <div style={{ padding: "8px 0" }}>
      <div className="row align-items-end">
        <div className="col-md-3 col-sm-6 mb-2">
          <TextField
            size="small"
            label="Buscar"
            placeholder="Buscar en todos los campos..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            variant="outlined"
            fullWidth
          />
        </div>
        <div className="col-md-3 col-sm-6 mb-2">
          <TextField
            size="small"
            label="Número de Socio"
            type="number"
            value={filterOptions.numeroSocio || ""}
            onChange={(e) =>
              setFilterOptions({ ...filterOptions, numeroSocio: e.target.value })
            }
            variant="outlined"
            fullWidth
          />
        </div>
        <div className="col-md-3 col-sm-6 mb-2">
          <label
            style={{
              display: "block",
              marginBottom: 4,
              fontWeight: 500,
              fontSize: 12,
              color: "#333",
            }}
          >
            Tipo de Usuario
          </label>
          <Select
            isMulti
            value={userTypes.filter((t) =>
              (filterOptions.tipoUsuario || []).includes(String(t._id))
            )}
            onChange={(selected) =>
              setFilterOptions({
                ...filterOptions,
                tipoUsuario: selected ? selected.map((s) => String(s._id)) : [],
              })
            }
            options={userTypes}
            getOptionLabel={(option) => option.titulo}
            getOptionValue={(option) => option._id}
            placeholder="Todos los tipos"
            isClearable
            styles={{
              control: (base) => ({ ...base, minHeight: 40 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
        <div className="col-md-3 col-sm-6 mb-2">
          <label
            style={{
              display: "block",
              marginBottom: 4,
              fontWeight: 500,
              fontSize: 12,
              color: "#333",
            }}
          >
            Provincia
          </label>
          <Select
            value={
              filterOptions.provincia
                ? provincias.find(
                    (p) => p.fullName === filterOptions.provincia
                  )
                : null
            }
            onChange={(selected) =>
              setFilterOptions({
                ...filterOptions,
                provincia: selected ? selected.fullName : null,
              })
            }
            options={provincias}
            getOptionLabel={(option) => option.fullName}
            getOptionValue={(option) => option.fullName}
            placeholder="Todas"
            isClearable
            styles={{
              control: (base) => ({ ...base, minHeight: 40 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
        <div className="col-md-3 col-sm-6 mb-2">
          <TextField
            size="small"
            label="Fecha Alta Desde"
            type="date"
            value={filterOptions.fechaAltaDesde || ""}
            onChange={(e) =>
              setFilterOptions({
                ...filterOptions,
                fechaAltaDesde: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            fullWidth
          />
        </div>
        <div className="col-md-3 col-sm-6 mb-2">
          <TextField
            size="small"
            label="Fecha Alta Hasta"
            type="date"
            value={filterOptions.fechaAltaHasta || ""}
            onChange={(e) =>
              setFilterOptions({
                ...filterOptions,
                fechaAltaHasta: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            fullWidth
          />
        </div>
      </div>
      <div className="row align-items-center">
        <div className="col-auto">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filterOptions.active}
                onChange={() =>
                  setFilterOptions({
                    ...filterOptions,
                    active: !filterOptions.active,
                  })
                }
              />
            }
            label={
              filterOptions.active ? "Solo activos" : "Todos (activos/inactivos)"
            }
          />
        </div>
        <div className="col-auto">
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Buscar
          </Button>
        </div>
        <div className="col-auto">
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterOffIcon />}
            onClick={handleClearFilters}
          >
            Limpiar
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="newsletter-users-dialog"
    >
      <DialogTitle id="newsletter-users-dialog">
        Seleccionar usuarios para enviar la newsletter
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ marginBottom: 16, backgroundColor: "#f8f9fa", padding: 12, borderRadius: 8 }}>
          {renderFiltersContent()}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Typography>Cargando usuarios...</Typography>
          </div>
        )}

        {!loading && (
          <>
            <BootstrapTable
              keyField="id"
              data={tableData}
              columns={columns}
              selectRow={selectRow}
              bordered={false}
              hover
              wrapperClasses="table-responsive"
              rowClasses="align-middle"
            />

            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 16,
                  marginTop: 16,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Typography variant="body2">
                  Página {currentPage} de {totalPages} ({totalUsers} usuarios)
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}

        {loadingSelectAll && (
          <Typography variant="body2" color="primary" style={{ marginTop: 12 }}>
            Cargando todos los usuarios...
          </Typography>
        )}
        {selectedIds.length > 0 && !loadingSelectAll && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            {selectedIds.length} usuario(s) seleccionado(s)
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            await onCancel?.();
            setOpen(false);
          }}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedIds.length === 0}
          onClick={() => {
            handleSend(selectedIds, templateId, newsletterId);
            setSelectedIds([]);
            setOpen(false);
          }}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsletterUsersSelectDialog;
